import React, { useState, useEffect } from "react";
import styled from '@emotion/styled/macro'
import { Flex, InputGroup, InputRightElement, IconButton } from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useForm } from "react-hook-form";
import { Navigate, Link } from 'react-router-dom';

import { Card, FormInput } from '../components/common';
import { ErrorText, Label, Input } from '../components/lib';
import { Button } from '@chakra-ui/react';
import { login, login2FA, getUserLogged } from '../api/auth';
import { useAuthState } from "../context/auth";
import { ERRORS } from '../utils/constants';
import * as mq from '../styles/media-queries';
import { redirectLoggedUserDefault } from "../routes";

const LoginContainer = styled.div`
    height: 100vh;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #e2e2e2;
    > div {
        margin: 0 auto;
        max-width: 400px;
        ${mq.small} {
            max-width: 300px;
        }
    }
    .logo {
        margin: 0 auto;
        width: 100%;
        padding: 10px 30px 20px 30px;
    }
`;

export const Login = () => {

    const { user, dispatch } = useAuthState()
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [otpRequired, setOtpRequired] = useState(false);
    const [tempToken, setTempToken] = useState(null);
    const [redirectTo, setRedirectTo] = useState(null);
    const [loggingIn, setLoggingIn] = useState(false);

    const { register, handleSubmit, formState: { errors } } = useForm();
    
    const onSubmit = async (values) => {
        setIsSubmitting(true);
        setError(null);
        if (otpRequired) {
            await handle2FA(values.otp_code);
        } else {
            await handleFormSubmit(values);
        }
    };

    const handle2FA = (otp_code) => {
        login2FA(tempToken, otp_code).then(res => {
            if (res.status !== 200) throw res;
            completeLogin(res.data);
        }).catch(error => {
            setError(error.response?.data?.message || 'Código inválido.');
            setIsSubmitting(false);
        });
    };

    const completeLogin = (data) => {
        setLoggingIn(true);
        dispatch({ type: "LOGIN", payload: data })
        getUserLogged().then(resUser => {
            const userLogged = resUser.data.data.user;
            dispatch({ type: "USER_LOGGED", payload: {user: userLogged, status: 'success'} })

            if (data.password_expired && userLogged.roles === 'paciente') {
                setRedirectTo('/admin/change-password');
            } else {
                const path = redirectLoggedUserDefault[userLogged.roles] || '/admin/patients';
                setRedirectTo(path);
            }
        }).catch(() => {
            setLoggingIn(false);
            setError(ERRORS.AUTH_LOGIN);
        }).finally(() => {
            setIsSubmitting(false);
        })
    };

    useEffect(() => {
        if(user && user.roles && redirectLoggedUserDefault[user.roles]) {
            setRedirectTo(redirectLoggedUserDefault[user.roles]);
        }
    },[user]);

    useEffect(() => {
        if(user && user.roles === 'paciente' && user.reset_password) {
            setRedirectTo('/admin/change-password');
        }
    },[user]);

    const handleFormSubmit = values => {
        login(values.email, values.password).then(res => {
            if (res.status !== 200) throw res;

            if (res.data.otp_required) {
                setTempToken(res.data.temp_token);
                setOtpRequired(true);
                setIsSubmitting(false);
                return;
            }

            completeLogin(res.data);
        })
            .catch(error => {
                setError(error.response?.data?.message || error.statusText || ERRORS.AUTH_LOGIN);
                setIsSubmitting(false);
            });
    };
    return (
        <>
            {redirectTo && <Navigate to={redirectTo} replace />}
            <LoginContainer>
                {loggingIn ? (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            Iniciando sesión...
                        </div>
                    </Card>
                ) : (
                <Card>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <img className="logo" alt="Logo" src="/img/logo_ziframed.png"/>

                        {!otpRequired ? (
                            <>
                    <FormInput 
                        autoFocus={true} 
                        label='Usuario o Email' 
                        type="text" 
                        {...register("email", { required: true })}
                        name="email" 
                        id="email" 
                    />

                        <div>
                            <Label>Contraseña</Label>
                            <InputGroup>
                                <Input 
                                    autoComplete="current-password" 
                                    type={showPassword ? "text" : "password"}
                                    {...register("password", { required: true })}
                                    name="password" 
                                    id="password" 
                                />
                                <InputRightElement>
                                    <IconButton
                                        variant="ghost"
                                        size="sm"
                                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                    />
                                </InputRightElement>
                            </InputGroup>
                        </div>
                            </>
                        ) : (
                            <>
                            <div style={{ textAlign: 'center', marginBottom: '16px', color: '#666', fontSize: '14px' }}>
                                Ingresá el código de verificación de tu app authenticator.
                            </div>
                            <FormInput 
                                autoFocus={true} 
                                label='Código de verificación' 
                                type="text" 
                                {...register("otp_code", { required: true, pattern: /^\d{6}$/ })}
                                name="otp_code" 
                                id="otp_code" 
                                placeholder="000000"
                                maxLength={6}
                            />
                            <Flex justify="center" mt="8px">
                                <Link to="#" onClick={(e) => { e.preventDefault(); setOtpRequired(false); setTempToken(null); }} style={{ color: '#553285', fontSize: '13px' }}>
                                    Volver al inicio de sesión
                                </Link>
                            </Flex>
                            </>
                        )}

                        <Flex justify="flex-end">
                            <Button variant="solid" colorScheme="purple" m={'5px 0px'} type="submit" size="lg" isLoading={isSubmitting} disabled={isSubmitting}> {otpRequired ? 'Verificar' : 'Ingresar'} </Button>
                        </Flex>

                        {error && (<ErrorText>{error}</ErrorText>)}

                        {!otpRequired && (
                            <Flex justify="center" mt="16px">
                                <Link to="/forgot-password" style={{ color: '#553285', fontSize: '14px' }}>
                                    ¿Olvidaste tu contraseña?
                                </Link>
                            </Flex>
                        )}

                        {/* [NGT 237 IX.B.3.c] El aviso va donde se pueda leer
                            ANTES de entrar al sistema, no solo dentro. */}
                        <Flex justify="center" mt="10px">
                            <Link to="/privacidad" style={{ color: '#718096', fontSize: '12px' }}>
                                Cómo tratamos tu información
                            </Link>
                        </Flex>
                    </form>
                </Card>
                )}
            </LoginContainer>
        </>
    );
};
export default Login;